import { GoogleGenAI, Modality } from "@google/genai";

export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

const base64ToGenerativePart = (base64, mimeType) => {
  return {
    inlineData: {
      data: base64.split(',')[1],
      mimeType,
    },
  };
};

const getAi = () => {
  const apiKey = process.env.REACT_APP_API_KEY;
  if (!apiKey) {
    throw new Error("REACT_APP_API_KEY environment variable not set");
  }
  return new GoogleGenAI({ apiKey });
}

export const generateProPhotos = async (base64Images, count, prompt) => {
  const ai = getAi();
  
  // Convert all base64 images to parts
  const imageParts = base64Images.map(image => 
    base64ToGenerativePart(image.base64, image.mimeType)
  );

  const promises = Array.from({ length: count }).map(async () => {
    const response = await ai.models.generateContent({
      model: 'gemini-2.5-flash-image',
      contents: {
        parts: [
          ...imageParts, // Multiple reference images
          { text: `Reference images provided. ${prompt}` },
        ],
      },
      config: {
        responseModalities: [Modality.IMAGE],
      },
    });

    const part = response.candidates?.[0]?.content?.parts?.[0];
    if (part?.inlineData) {
      const { data, mimeType } = part.inlineData;
      return {
        id: crypto.randomUUID(),
        base64: `data:${mimeType};base64,${data}`,
        mimeType: mimeType,
      };
    }
    throw new Error('Image generation failed to return valid data.');
  });
  return Promise.all(promises);
};

// Keep other functions the same...
export const editImageWithPrompt = async (image, prompt) => {
  const ai = getAi();
  const imagePart = base64ToGenerativePart(image.base64, image.mimeType);
  
  const response = await ai.models.generateContent({
    model: 'gemini-2.5-flash-image',
    contents: {
      parts: [imagePart, { text: prompt }],
    },
    config: {
      responseModalities: [Modality.IMAGE],
    },
  });

  const part = response.candidates?.[0]?.content?.parts?.[0];
  if (part?.inlineData) {
    const { data, mimeType } = part.inlineData;
    return {
      id: image.id,
      base64: `data:${mimeType};base64,${data}`,
      mimeType: mimeType,
    };
  }
  throw new Error('Image editing failed.');
};

export const generateProductVideo = async (uploadedFiles, aspectRatio, prompt) => {
  const ai = getAi();
  // Use the first file for video generation, or implement multi-image video generation
  const firstFile = uploadedFiles[0];
  const operation = await ai.models.generateVideos({
    model: 'veo-3.1-fast-generate-preview',
    prompt: prompt,
    image: {
      imageBytes: firstFile.base64.split(',')[1],
      mimeType: firstFile.mimeType,
    },
    config: {
      numberOfVideos: 1,
      resolution: '720p',
      aspectRatio: aspectRatio,
    },
  });
  return operation;
};

export const pollVideoStatus = async (operation) => {
  const ai = getAi();
  return await ai.operations.getVideosOperation({ operation: operation });
}